(function () {
  if (window.__vccBrandBarLoaded) return;
  window.__vccBrandBarLoaded = true;

  const selector = '[data-vcc-brand-bar]';
  const states = new WeakMap();
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const dragIntentDistance = 8;
  const dragAxisDominance = 1.25;

  function readNumber(value, fallback) {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
  }

  const hoverImageLoads = new Map();

  function preloadHoverSource(source, sourceSet) {
    const key = source + '\n' + (sourceSet || '');
    const existingLoad = hoverImageLoads.get(key);

    if (existingLoad) return existingLoad;

    const load = new Promise((resolve) => {
      const loader = new Image();
      let settled = false;

      const finish = (loaded) => {
        if (settled) return;
        settled = true;
        resolve(loaded);
      };

      loader.addEventListener('load', () => finish(true), { once: true });
      loader.addEventListener('error', () => finish(false), { once: true });

      if (sourceSet) loader.srcset = sourceSet;
      loader.src = source;

      if (loader.complete) finish(loader.naturalWidth > 0);
    });

    hoverImageLoads.set(key, load);
    load.then((loaded) => {
      if (!loaded) hoverImageLoads.delete(key);
    });

    return load;
  }

  function waitForHoverImage(image) {
    const decode = () => {
      const decoded = typeof image.decode === 'function'
        ? image.decode().catch(() => undefined)
        : Promise.resolve();

      return decoded.then(() => image.complete && image.naturalWidth > 0);
    };

    if (image.complete) {
      return image.naturalWidth > 0 ? decode() : Promise.resolve(false);
    }

    return new Promise((resolve) => {
      image.addEventListener('load', () => resolve(true), { once: true });
      image.addEventListener('error', () => resolve(false), { once: true });
    }).then((loaded) => loaded && decode());
  }

  function hydrateHoverImage(image) {
    if (!image || image.dataset.vccHoverReady === 'true') return Promise.resolve(true);
    if (image.vccHoverLoad) return image.vccHoverLoad;

    const source = image.dataset.vccHoverSrc;
    const sourceSet = image.dataset.vccHoverSrcset || '';

    if (!source) return Promise.resolve(false);

    image.vccHoverLoad = preloadHoverSource(source, sourceSet).then((loaded) => {
      if (!loaded) {
        image.vccHoverLoad = null;
        return false;
      }

      if (sourceSet) image.srcset = sourceSet;
      image.src = source;

      return waitForHoverImage(image).then((ready) => {
        if (!ready || !image.isConnected) {
          image.vccHoverLoad = null;
          return false;
        }

        image.dataset.vccHoverReady = 'true';
        image.removeAttribute('data-vcc-hover-src');
        image.removeAttribute('data-vcc-hover-srcset');

        const stack = image.closest('.vcc-brand-bar__logo-stack--has-hover');
        if (stack) stack.classList.add('vcc-brand-bar__logo-stack--hover-ready');

        return true;
      });
    });

    return image.vccHoverLoad;
  }

  function hydrateItemHoverImage(item) {
    if (!item) return;

    const image = item.querySelector('.vcc-brand-bar__logo--hover[data-vcc-hover-src]');
    if (image) hydrateHoverImage(image);
  }

  function resolveDragAxis(absX, absY) {
    if (Math.max(absX, absY) < dragIntentDistance) return null;
    if (absX >= absY * dragAxisDominance) return 'x';
    if (absY >= absX * dragAxisDominance) return 'y';
    return null;
  }

  function easeInOutCubic(value) {
    return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
  }

  function getDuration(root) {
    const desktop = readNumber(root.dataset.stopStartDuration, 650);
    return mobileQuery.matches ? readNumber(root.dataset.stopStartDurationMobile, desktop) : desktop;
  }

  function getPause(root) {
    const desktop = readNumber(root.dataset.stopStartPause, 900);
    return mobileQuery.matches ? readNumber(root.dataset.stopStartPauseMobile, desktop) : desktop;
  }

  function getClientX(event) {
    if (event.touches && event.touches.length) return event.touches[0].clientX;
    if (event.changedTouches && event.changedTouches.length) return event.changedTouches[0].clientX;
    return event.clientX;
  }

  function getClientY(event) {
    if (event.touches && event.touches.length) return event.touches[0].clientY;
    if (event.changedTouches && event.changedTouches.length) return event.changedTouches[0].clientY;
    return event.clientY;
  }

  function isPointerFocusSuppressed(state) {
    return state.pointerDown || state.pointerFocusUntil > performance.now();
  }

  function suppressPointerFocus(state, duration) {
    state.pointerFocusUntil = performance.now() + duration;
  }

  function blurActiveLogo(root) {
    const active = document.activeElement;

    if (active && active !== document.body && root.contains(active) && typeof active.blur === 'function') {
      active.blur();
    }
  }

  function getMaxScroll(viewport) {
    return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  }

  function getLoopWidth(state) {
    if (!state.set || !state.clone) return 0;

    const trackStyle = window.getComputedStyle(state.track);
    const gap = readNumber(trackStyle.columnGap || trackStyle.gap, 0);
    const setWidth = state.set.getBoundingClientRect().width;
    const cloneWidth = state.clone.getBoundingClientRect().width;

    if (!setWidth || Math.abs(setWidth - cloneWidth) > 1) return 0;
    return setWidth + gap;
  }

  function setScrollLeftImmediately(state, target) {
    if (Math.abs(state.viewport.scrollLeft - target) < 0.5) return;

    const previousScrollBehavior = state.viewport.style.scrollBehavior;
    const previousScrollSnapType = state.viewport.style.scrollSnapType;

    state.normalizingSmooth = true;
    state.viewport.style.scrollBehavior = 'auto';
    state.viewport.style.scrollSnapType = 'none';
    state.viewport.scrollLeft = target;

    if (previousScrollBehavior) {
      state.viewport.style.scrollBehavior = previousScrollBehavior;
    } else {
      state.viewport.style.removeProperty('scroll-behavior');
    }

    if (previousScrollSnapType) {
      state.viewport.style.scrollSnapType = previousScrollSnapType;
    } else {
      state.viewport.style.removeProperty('scroll-snap-type');
    }

    if (state.smoothNormalizationFrame) {
      window.cancelAnimationFrame(state.smoothNormalizationFrame);
    }

    state.smoothNormalizationFrame = window.requestAnimationFrame(() => {
      state.smoothNormalizationFrame = null;
      state.normalizingSmooth = false;
    });
  }

  function normalizeSmoothLoop(state) {
    if (state.mode !== 'smooth' || !state.clone) return true;

    const loopWidth = getLoopWidth(state);
    const maxScroll = getMaxScroll(state.viewport);

    // Four matching sets must leave two full loops beyond the viewport: one
    // for the current frame and one for the running 0 -> -loop animation.
    if (!loopWidth || maxScroll < loopWidth * 2 - 1) return false;

    const normalized = ((state.viewport.scrollLeft % loopWidth) + loopWidth) % loopWidth;
    setScrollLeftImmediately(state, normalized);
    return true;
  }

  function queueSmoothLoopNormalization(root, state) {
    if (state.mode !== 'smooth') return;

    if (state.smoothLayoutFrame) {
      window.cancelAnimationFrame(state.smoothLayoutFrame);
    }

    state.smoothLayoutFrame = window.requestAnimationFrame(() => {
      state.smoothLayoutFrame = null;
      if (state.stopped || state.pointerDown || state.nativeTouchActive) return;

      const wasGeometryBlocked = state.smoothGeometryBlocked;

      if (!normalizeSmoothLoop(state)) {
        state.smoothGeometryBlocked = true;
        state.paused = true;
        setPausedClass(root, true);
        return;
      }

      state.smoothGeometryBlocked = false;

      if (wasGeometryBlocked && !reducedMotionQuery.matches && canResume(state)) {
        state.paused = false;
        setPausedClass(root, false);
      }
    });
  }

  function rebaseLoopDrag(state) {
    if (state.mode === 'smooth') {
      normalizeSmoothLoop(state);
      return;
    }

    const loopWidth = getLoopWidth(state);
    const maxScroll = getMaxScroll(state.viewport);

    if (!loopWidth || maxScroll < loopWidth) return;

    // Start in the duplicate copy so an identical sequence exists on both sides.
    state.viewport.scrollLeft = clamp(loopWidth, 0, maxScroll);
  }

  function wrapLoopDrag(state, nextScrollLeft) {
    const loopWidth = getLoopWidth(state);
    const maxScroll = getMaxScroll(state.viewport);

    if (!loopWidth || maxScroll < loopWidth) {
      return clamp(nextScrollLeft, 0, maxScroll);
    }

    const lowerBound = state.mode === 'smooth' ? 0 : loopWidth * 0.2;
    const upperBound = state.mode === 'smooth'
      ? Math.max(lowerBound, maxScroll - loopWidth)
      : Math.max(lowerBound, maxScroll - loopWidth * 0.2);

    while (nextScrollLeft < lowerBound) {
      nextScrollLeft += loopWidth;
      state.dragStartScrollLeft += loopWidth;
    }

    while (nextScrollLeft > upperBound) {
      nextScrollLeft -= loopWidth;
      state.dragStartScrollLeft -= loopWidth;
    }

    return clamp(nextScrollLeft, 0, maxScroll);
  }

  function normalizeStopStartLoop(state) {
    if (state.mode !== 'stop-start') return;

    const loopWidth = getLoopWidth(state);
    if (!loopWidth) return;

    state.viewport.scrollLeft = ((state.viewport.scrollLeft % loopWidth) + loopWidth) % loopWidth;
  }

  function getTargetScrollLeft(viewport, item) {
    const viewportRect = viewport.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const itemCenter = viewport.scrollLeft + itemRect.left - viewportRect.left + itemRect.width / 2;

    return clamp(itemCenter - viewport.clientWidth / 2, 0, getMaxScroll(viewport));
  }

  function syncIndex(state) {
    if (!state.items.length) return;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    state.items.forEach((item, index) => {
      const distance = Math.abs(state.viewport.scrollLeft - getTargetScrollLeft(state.viewport, item));

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    state.index = closestIndex;
  }

  function clearTimer(state) {
    if (!state.timer) return;
    window.clearTimeout(state.timer);
    state.timer = null;
  }

  function clearScrollTimer(state) {
    if (!state.scrollTimer) return;
    window.clearTimeout(state.scrollTimer);
    state.scrollTimer = null;
  }

  function clearClickTimer(state) {
    if (!state.clickTimer) return;
    window.clearTimeout(state.clickTimer);
    state.clickTimer = null;
  }

  function cancelAnimation(state) {
    state.animationToken += 1;

    if (state.animationFrame) {
      window.cancelAnimationFrame(state.animationFrame);
      state.animationFrame = null;
    }

    state.programmaticScroll = false;
  }

  function setPausedClass(root, paused) {
    root.classList.toggle('vcc-brand-bar--js-paused', paused);
  }

  function canResume(state) {
    return !state.stopped && !state.overlayPaused && !state.hovering && !state.focused
      && !state.pointerDown && !state.nativeTouchActive;
  }

  function schedule(root, state, delay) {
    clearTimer(state);

    if (state.stopped || state.mode !== 'stop-start' || reducedMotionQuery.matches || !canResume(state)) return;

    state.timer = window.setTimeout(() => tick(root, state), delay);
  }

  function pause(root, state) {
    if (state.stopped) return;

    state.paused = true;
    clearTimer(state);
    cancelAnimation(state);
    setPausedClass(root, true);
    syncIndex(state);
  }

  function resume(root, state, delay) {
    if (!canResume(state)) return;

    if (state.mode === 'smooth' && !normalizeSmoothLoop(state)) {
      state.smoothGeometryBlocked = true;
      state.paused = true;
      setPausedClass(root, true);
      return;
    }

    state.smoothGeometryBlocked = false;

    syncIndex(state);
    state.paused = false;
    setPausedClass(root, false);
    schedule(root, state, delay);
  }

  function forceSmoothResume(root, state) {
    if (state.stopped || state.mode !== 'smooth' || reducedMotionQuery.matches || !canResume(state)) return;

    if (!normalizeSmoothLoop(state)) {
      state.smoothGeometryBlocked = true;
      state.paused = true;
      setPausedClass(root, true);
      return;
    }

    state.smoothGeometryBlocked = false;

    state.paused = false;
    clearTimer(state);
    clearScrollTimer(state);
    markDragging(root, state, false);
    setPausedClass(root, false);
  }

  function queueSmoothResume(root, state, delay) {
    if (state.mode !== 'smooth') return;

    window.setTimeout(() => forceSmoothResume(root, state), delay);
  }

  function animateScroll(viewport, target, duration, state) {
    cancelAnimation(state);

    const start = viewport.scrollLeft;
    const distance = target - start;
    const token = state.animationToken;

    if (duration <= 0 || Math.abs(distance) < 1) {
      viewport.scrollLeft = target;
      return Promise.resolve(true);
    }

    state.programmaticScroll = true;

    return new Promise((resolve) => {
      const startedAt = performance.now();

      const finish = (completed) => {
        if (token === state.animationToken) {
          state.animationFrame = null;
          state.programmaticScroll = false;
        }

        resolve(completed);
      };

      const step = (now) => {
        if (state.stopped || state.paused || token !== state.animationToken) {
          finish(false);
          return;
        }

        const progress = clamp((now - startedAt) / duration, 0, 1);
        viewport.scrollLeft = start + distance * easeInOutCubic(progress);

        if (progress < 1) {
          state.animationFrame = window.requestAnimationFrame(step);
          return;
        }

        finish(true);
      };

      state.animationFrame = window.requestAnimationFrame(step);
    });
  }

  async function tick(root, state) {
    if (state.stopped || state.paused || state.mode !== 'stop-start') return;

    if (reducedMotionQuery.matches || state.viewport.scrollWidth <= state.viewport.clientWidth + 2) {
      schedule(root, state, getPause(root));
      return;
    }

    syncIndex(state);
    state.index = (state.index + 1) % state.items.length;

    const item = state.items[state.index];
    const target = getTargetScrollLeft(state.viewport, item);
    const completed = await animateScroll(state.viewport, target, getDuration(root), state);

    if (completed && !state.paused) {
      schedule(root, state, getPause(root));
    }
  }

  function markDragging(root, state, dragging) {
    state.dragging = dragging;
    root.classList.toggle('vcc-brand-bar--dragging', dragging);
  }

  function beginPointerDrag(root, state, event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.pointerType === 'touch' || event.pointerType === 'pen') return;
    if (getMaxScroll(state.viewport) <= 0) return;

    state.pointerDown = true;
    state.dragging = false;
    state.dragAxis = null;
    state.dragStartX = getClientX(event);
    state.dragStartY = getClientY(event);
    state.dragStartScrollLeft = state.viewport.scrollLeft;
    state.pointerId = event.pointerId;
    state.pointerType = event.pointerType || 'mouse';
    state.pointerCaptured = false;
    state.ignoreClick = false;
    state.dragRebased = false;
    state.focused = false;
    suppressPointerFocus(state, 900);

    pause(root, state);
  }

  function beginNativeTouch(root, state, event) {
    if (!event.touches || !event.touches.length || getMaxScroll(state.viewport) <= 0) return;

    const touch = event.touches[0];
    clearScrollTimer(state);
    clearClickTimer(state);
    state.ignoreClick = false;
    state.nativeTouchActive = true;
    state.nativeTouchMoved = false;
    state.nativeTouchStartX = touch.clientX;
    state.nativeTouchStartY = touch.clientY;
    state.nativeTouchStartScrollLeft = state.viewport.scrollLeft;
    state.focused = false;
    suppressPointerFocus(state, 900);
    pause(root, state);
  }

  function moveNativeTouch(state, event) {
    if (!state.nativeTouchActive || !event.touches || !event.touches.length) return;

    const touch = event.touches[0];
    const absX = Math.abs(touch.clientX - state.nativeTouchStartX);
    const absY = Math.abs(touch.clientY - state.nativeTouchStartY);
    const scrollDistance = Math.abs(state.viewport.scrollLeft - state.nativeTouchStartScrollLeft);

    if ((absX > 6 && absX >= absY) || scrollDistance > 6) {
      state.nativeTouchMoved = true;
    }
  }

  function endNativeTouch(root, state, event) {
    if (!state.nativeTouchActive || (event.touches && event.touches.length)) return;

    const wasDragging = state.nativeTouchMoved
      || Math.abs(state.viewport.scrollLeft - state.nativeTouchStartScrollLeft) > 6;

    state.nativeTouchActive = false;
    state.nativeTouchMoved = false;

    if (wasDragging) {
      state.ignoreClick = true;
      clearClickTimer(state);
      state.clickTimer = window.setTimeout(() => {
        state.ignoreClick = false;
      }, 700);
      blurActiveLogo(root);
      handleScroll(root, state);
      return;
    }

    resume(root, state, Math.max(600, getPause(root)));
    queueSmoothResume(root, state, 260);
  }

  function movePointerDrag(root, state, event) {
    if (!state.pointerDown) return;

    const x = getClientX(event);
    const y = getClientY(event);
    const deltaX = x - state.dragStartX;
    const deltaY = y - state.dragStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (!state.dragAxis) {
      state.dragAxis = resolveDragAxis(absX, absY);
      if (!state.dragAxis) return;
    }

    if (state.dragAxis !== 'x') return;

    if (!state.dragging) {
      if (!state.pointerCaptured && event.pointerId !== undefined && state.viewport.setPointerCapture) {
        try {
          state.viewport.setPointerCapture(event.pointerId);
          state.pointerCaptured = true;
        } catch (error) {
          // Pointer capture can fail if the browser has already cancelled the pointer.
        }
      }

      if (!state.dragRebased) {
        rebaseLoopDrag(state);
        state.dragStartScrollLeft = state.viewport.scrollLeft;
        state.dragRebased = true;
      }

      markDragging(root, state, true);
    }

    if (event.cancelable && state.pointerType === 'mouse') event.preventDefault();

    state.ignoreClick = absX > 6;
    const nextScrollLeft = state.dragStartScrollLeft - deltaX;
    state.viewport.scrollLeft = state.clone
      ? wrapLoopDrag(state, nextScrollLeft)
      : clamp(nextScrollLeft, 0, getMaxScroll(state.viewport));
  }

  function endPointerDrag(root, state, event) {
    if (!state.pointerDown) return;

    if (state.pointerCaptured && event && event.pointerId !== undefined && state.viewport.releasePointerCapture) {
      try {
        state.viewport.releasePointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture may already be released on cancel/leave.
      }
    }
    state.pointerCaptured = false;

    const wasDragging = state.dragging || state.ignoreClick;
    const wasTouchLike = state.pointerType === 'touch' || state.pointerType === 'pen';

    state.pointerDown = false;
    state.focused = false;
    suppressPointerFocus(state, 900);

    if (wasDragging || wasTouchLike) {
      blurActiveLogo(root);
    }

    if (wasDragging) {
      state.ignoreClick = true;
      clearClickTimer(state);
      state.clickTimer = window.setTimeout(() => {
        state.ignoreClick = false;
      }, 220);
    }

    markDragging(root, state, false);
    normalizeStopStartLoop(state);
    normalizeSmoothLoop(state);
    syncIndex(state);
    resume(root, state, Math.max(600, getPause(root)));
    queueSmoothResume(root, state, 80);
    queueSmoothResume(root, state, 260);
  }

  function handleScroll(root, state) {
    if (state.stopped || state.programmaticScroll || state.normalizingSmooth || state.pointerDown) return;

    pause(root, state);
    clearScrollTimer(state);

    state.scrollTimer = window.setTimeout(() => {
      state.scrollTimer = null;
      if (state.nativeTouchActive) return;

      normalizeSmoothLoop(state);
      syncIndex(state);
      state.focused = false;
      resume(root, state, Math.max(600, getPause(root)));
      queueSmoothResume(root, state, 80);
    }, 160);
  }

  function listen(state, target, eventName, handler, options) {
    target.addEventListener(eventName, handler, options);
    state.cleanup.push(() => target.removeEventListener(eventName, handler, options));
  }

  function teardown(root) {
    const state = states.get(root);
    if (!state) return;

    state.stopped = true;
    clearTimer(state);
    clearScrollTimer(state);
    clearClickTimer(state);
    cancelAnimation(state);

    if (state.smoothLayoutFrame) {
      window.cancelAnimationFrame(state.smoothLayoutFrame);
      state.smoothLayoutFrame = null;
    }

    if (state.smoothNormalizationFrame) {
      window.cancelAnimationFrame(state.smoothNormalizationFrame);
      state.smoothNormalizationFrame = null;
    }

    state.normalizingSmooth = false;
    root.classList.remove('vcc-brand-bar--dragging', 'vcc-brand-bar--js-paused');

    state.cleanup.forEach((cleanup) => cleanup());
    states.delete(root);
  }

  function init(root) {
    if (!root) return;

    teardown(root);

    const viewport = root.querySelector('[data-vcc-brand-bar-logos]');
    const set = root.querySelector('[data-vcc-brand-bar-set]');
    const clone = root.querySelector('[data-vcc-brand-bar-clone]');
    const track = root.querySelector('[data-vcc-brand-bar-track]');
    const items = set ? Array.from(set.querySelectorAll('.vcc-brand-bar__item')) : [];

    if (!viewport || !track || items.length < 2) return;

    const state = {
      animationFrame: null,
      animationToken: 0,
      cleanup: [],
      clickTimer: null,
      dragAxis: null,
      dragging: false,
      focused: false,
      hovering: false,
      ignoreClick: false,
      dragRebased: false,
      index: 0,
      items,
      mode: root.dataset.logoScrollMode,
      nativeTouchActive: false,
      nativeTouchMoved: false,
      nativeTouchStartScrollLeft: 0,
      nativeTouchStartX: 0,
      nativeTouchStartY: 0,
      overlayPaused: document.documentElement.classList.contains('vcc-overlay-open'),
      paused: false,
      pointerDown: false,
      pointerFocusUntil: 0,
      pointerType: 'mouse',
      programmaticScroll: false,
      normalizingSmooth: false,
      scrollTimer: null,
      smoothGeometryBlocked: false,
      smoothLayoutFrame: null,
      smoothNormalizationFrame: null,
      stopped: false,
      clone,
      set,
      timer: null,
      track,
      viewport,
    };

    states.set(root, state);
    syncIndex(state);
    queueSmoothLoopNormalization(root, state);

    listen(state, root, 'pointerover', (event) => {
      if (event.pointerType !== 'mouse' || !event.target.closest) return;

      const item = event.target.closest('.vcc-brand-bar__item');
      if (item && root.contains(item)) hydrateItemHoverImage(item);
    });

    listen(state, root, 'focusin', (event) => {
      if (!event.target.closest || !event.target.matches(':focus-visible')) return;

      const item = event.target.closest('.vcc-brand-bar__item');
      if (item && root.contains(item)) hydrateItemHoverImage(item);
    });

    const pauseOnHover = root.dataset.pauseOnHover === 'true';

    if (pauseOnHover) {
      listen(state, root, 'pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        state.hovering = true;
        pause(root, state);
      });

      listen(state, root, 'pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        state.hovering = false;
        resume(root, state, 180);
      });

      listen(state, root, 'focusin', () => {
        if (isPointerFocusSuppressed(state)) {
          state.focused = false;
          queueSmoothResume(root, state, 80);
          return;
        }

        state.focused = true;
        pause(root, state);
      });

      listen(state, root, 'focusout', () => {
        state.focused = false;
        resume(root, state, 180);
        queueSmoothResume(root, state, 80);
      });
    }

    listen(state, viewport, 'pointerdown', (event) => beginPointerDrag(root, state, event));
    listen(state, viewport, 'pointermove', (event) => movePointerDrag(root, state, event), { passive: false });
    listen(state, viewport, 'pointerup', (event) => endPointerDrag(root, state, event));
    listen(state, viewport, 'pointercancel', (event) => endPointerDrag(root, state, event));
    listen(state, viewport, 'lostpointercapture', (event) => endPointerDrag(root, state, event));
    listen(state, viewport, 'touchstart', (event) => beginNativeTouch(root, state, event), { passive: true });
    listen(state, viewport, 'touchmove', (event) => moveNativeTouch(state, event), { passive: true });
    listen(state, viewport, 'touchend', (event) => endNativeTouch(root, state, event), { passive: true });
    listen(state, viewport, 'touchcancel', (event) => endNativeTouch(root, state, event), { passive: true });

    listen(state, viewport, 'click', (event) => {
      if (!state.ignoreClick) return;

      event.preventDefault();
      event.stopPropagation();
      state.ignoreClick = false;
    }, true);

    listen(state, viewport, 'wheel', () => {
      pause(root, state);
      clearScrollTimer(state);
      state.scrollTimer = window.setTimeout(() => {
        syncIndex(state);
        state.focused = false;
        resume(root, state, Math.max(700, getPause(root)));
        queueSmoothResume(root, state, 80);
      }, 180);
    }, { passive: true });

    listen(state, viewport, 'scroll', () => handleScroll(root, state), { passive: true });

    listen(state, window, 'resize', () => queueSmoothLoopNormalization(root, state), { passive: true });
    listen(state, window, 'pageshow', () => queueSmoothLoopNormalization(root, state));
    listen(state, document, 'visibilitychange', () => {
      if (document.visibilityState === 'visible') queueSmoothLoopNormalization(root, state);
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => queueSmoothLoopNormalization(root, state));
      resizeObserver.observe(viewport);
      resizeObserver.observe(set);
      state.cleanup.push(() => resizeObserver.disconnect());
    }

    listen(state, document, 'vcc:overlay-state', (event) => {
      const overlayOpen = Boolean(event.detail && event.detail.open);
      state.overlayPaused = overlayOpen;

      if (overlayOpen) {
        pause(root, state);
        return;
      }

      resume(root, state, Math.max(300, getPause(root)));
      queueSmoothResume(root, state, 80);
    });

    if (state.overlayPaused) {
      pause(root, state);
    } else if (state.mode === 'stop-start' && !reducedMotionQuery.matches) {
      schedule(root, state, getPause(root));
    }
  }

  function initAll(container) {
    const root = container.matches && container.matches(selector) ? container : null;
    const roots = root ? [root] : Array.from(container.querySelectorAll(selector));

    roots.forEach(init);
  }

  initAll(document);
  document.addEventListener('DOMContentLoaded', () => initAll(document));

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches(selector) ? event.target : null;
    const roots = root ? [root] : Array.from(event.target.querySelectorAll(selector));

    roots.forEach(teardown);
  });
})();
